// import atoms
import { planResponsePlanningEntityIdentifiersAtom } from '../../atoms';

// import components
import { Button, Collapse, DialogActions, Typography } from '@mui/material';
import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledTimePicker,
  Dialog
} from 'components/shared';
import {
  GridCompact12,
  GridCompact6,
  GridContainer,
  GridContainerMediumMaxWidth
} from 'components/shared/Grid/Grid.components';
import { showSuccessSnackbar } from 'components/shared/Snackbar';
import { StyledNoteDeleteEntirePeriodBox } from './DeleteAbsencePeriodDialog.styles';
import { Icon } from '@sdb/planning-ui';

// import external libraries
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

// import hooks
import {
  useDeleteClientAbsencePeriod,
  useEndClientAbsencePeriod
} from '../../mutations';

// import types
import {
  DeleteAbsencePeriodDialogForm,
  DeleteAbsencePeriodDialogProps
} from './DeleteAbsencePeriodDialog.types';

// import utils
import { refetchPlanResponsePlanningEntityIdentifiers } from '../../utils';

// import validation
import { schema } from './delete-absence-periods-schema';

export default function DeleteAbsencePeriodDialog({
  open,
  handleClose,
  title,
  clientId,
  clientAbsence
}: DeleteAbsencePeriodDialogProps) {
  const { t } = useTranslation('common');
  const { mutate: endClientAbsencePeriod } = useEndClientAbsencePeriod();
  const { mutate: deleteClientAbsencePeriod } = useDeleteClientAbsencePeriod();
  const { control, handleSubmit, watch, reset } =
    useForm<DeleteAbsencePeriodDialogForm>({
      resolver: yupResolver(schema)
    });
  const { deletePeriod } = watch();
  const [, setPlanResponsePlanningEntityIdentifiers] = useRecoilState(
    planResponsePlanningEntityIdentifiersAtom
  );

  useEffect(() => {
    reset({
      deletePeriod: false,
      endDate: dayjs().startOf('day'),
      endTime: dayjs(null)
    });
  }, [clientAbsence?.end]);

  function onSubmit(data: DeleteAbsencePeriodDialogForm) {
    const { deletePeriod, endDate, endTime } = data;
    if (!clientId || !clientAbsence) return;

    const basePayload = {
      clientId,
      clientAbsenceId: clientAbsence.clientAbsenceId
    };

    const onComplete = () => {
      showSuccessSnackbar();
      refetchPlanResponsePlanningEntityIdentifiers(
        setPlanResponsePlanningEntityIdentifiers
      );
      handleClose();
    };

    if (deletePeriod) {
      deleteClientAbsencePeriod(basePayload, {
        onSuccess: onComplete
      });
    } else {
      const newEnd = endDate.hour(endTime.hour()).minute(endTime.minute());

      const payload = {
        ...basePayload,
        end: newEnd.toISOString()
      };

      endClientAbsencePeriod(payload, {
        onSuccess: onComplete
      });
    }
  }

  return (
    <Dialog open={open} handleClose={handleClose} title={title}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <GridContainerMediumMaxWidth>
          <GridCompact12>
            <ControlledCheckbox
              control={control}
              name="deletePeriod"
              label={t('deleteEntirePeriod')}
            />
          </GridCompact12>
          <GridCompact12>
            <Collapse in={deletePeriod}>
              <StyledNoteDeleteEntirePeriodBox>
                <Icon type="triangle-exclamation" color="warning" />
                <Typography fontSize="small">
                  {t('noteDeleteEntirePeriod')}
                </Typography>
              </StyledNoteDeleteEntirePeriodBox>
            </Collapse>
          </GridCompact12>
          <GridCompact12>
            <Collapse in={!deletePeriod}>
              <GridContainer>
                <GridCompact6>
                  <ControlledDatePicker
                    name="endDate"
                    control={control}
                    label={t('endDate')}
                    fullWidth
                  />
                </GridCompact6>
                <GridCompact6>
                  <ControlledTimePicker
                    name="endTime"
                    control={control}
                    label={t('endTime')}
                    fullWidth
                  />
                </GridCompact6>
              </GridContainer>
            </Collapse>
          </GridCompact12>
        </GridContainerMediumMaxWidth>
        <DialogActions>
          <Button
            type="button"
            variant="text"
            color="primary"
            onClick={handleClose}
          >
            {t('cancel')}
          </Button>
          <Button type="submit" variant="contained" color="primary">
            {t('save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
